import { QueryClient, QueryFunction, QueryKey } from "@tanstack/react-query";

/**
 * Validates response and throws appropriate error
 * @param res - Fetch Response object
 * @throws {Error} With status code and response text if request failed
 */
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Determines the appropriate cache strategy based on the URL
 * @param url - Request URL
 * @returns {RequestCache} Cache strategy to use
 */
function getCacheStrategy(url: string): RequestCache {
  // Static resources like translations, images, etc. can use force-cache
  if (url.includes('/locales/') || 
      url.includes('/assets/') || 
      url.endsWith('.svg') || 
      url.endsWith('.png') || 
      url.endsWith('.jpg') || 
      url.endsWith('.webp')) {
    return 'force-cache';
  }
  
  // API endpoints should use no-cache to ensure fresh data
  if (url.includes('/api/')) {
    return 'no-cache';
  }
  
  // Default to network-first approach for other resources
  return 'default';
}

/**
 * Makes an API request with proper error handling, retry logic, and type safety
 * @param method - HTTP method (GET, POST, etc)
 * @param url - API endpoint URL
 * @param data - Optional request body data
 * @param retries - Number of retries to attempt (default: 2)
 * @returns {Promise<Response>} Fetch Response object
 * @throws {Error} If request fails or returns non-200 status after retries
 */
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  retries = 2
): Promise<Response> {
  // Set up request headers
  const headers: HeadersInit = data 
    ? { "Content-Type": "application/json" } 
    : {};
  
  // Add network timeout for better UX
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000); // 20-second timeout

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: "include",
      // Use appropriate cache strategy based on request type
      cache: method.toUpperCase() === 'GET' ? getCacheStrategy(url) : 'no-cache',
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    await throwIfResNotOk(res);
    return res;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // NetworkError or AbortError indicates connection issues
    const isNetworkError = 
      error instanceof TypeError || 
      (error instanceof DOMException && error.name === 'AbortError');
    
    // Retry only for network errors and if retries remain
    if (isNetworkError && retries > 0) {
      console.info(`[API] Retrying request to ${url}, ${retries} attempts remaining`);
      
      // Exponential backoff - wait longer between each retry
      const backoffDelay = 1000 * (2 ** (2 - retries));
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
      
      // Try again with one less retry
      return apiRequest(method, url, data, retries - 1);
    }
    
    // No more retries or non-network error, propagate the error
    throw error;
  }
}

/**
 * Behavior options for handling 401 unauthorized responses
 */
type UnauthorizedBehavior = "returnNull" | "throw";

/**
 * Creates a query function with customizable unauthorized response handling
 * @param options - Configuration options for query behavior
 * @returns {QueryFunction} Function for use with react-query
 */
/**
 * Creates a query function with customizable unauthorized response handling
 * @generic TData - The data type that will be returned by the query
 */
export const getQueryFn = <TData>(options: {
  on401: UnauthorizedBehavior;
  retries?: number;
}): QueryFunction<TData> => {
  const { on401: unauthorizedBehavior, retries = 2 } = options;
  
  return async (context) => {
    const { queryKey } = context;
    const url = queryKey[0] as string;
    
    // Add network timeout for better UX
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 20-second timeout
    
    try {
      const res = await fetch(url, {
        credentials: "include",
        cache: getCacheStrategy(url), // Use dynamic cache strategy
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null as any; // Cast needed due to null return with generic type
      }

      await throwIfResNotOk(res);
      return await res.json() as TData;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // NetworkError or AbortError indicates connection issues
      const isNetworkError = 
        error instanceof TypeError || 
        (error instanceof DOMException && error.name === 'AbortError');
      
      // Retry only for network errors and if retries remain
      if (isNetworkError && retries > 0) {
        console.info(`[Query] Retrying request to ${url}, ${retries} attempts remaining`);
        
        // Exponential backoff - wait longer between each retry
        const backoffDelay = 1000 * (2 ** (2 - retries));
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        
        // Try again with one less retry by creating a new query function with decremented retries
        const retryFn = getQueryFn<TData>({ 
          on401: unauthorizedBehavior, 
          retries: retries - 1 
        });
        return retryFn(context);
      }
      
      // No more retries or non-network error, propagate the error
      throw error;
    }
  };
};

/**
 * Configured QueryClient instance with optimized settings
 * - Implements caching strategy
 * - Handles unauthorized responses
 * - Manages stale time and garbage collection
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 30 * 60 * 1000, // Cache garbage collection time (replaces deprecated cacheTime)
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});